import { TARGET_TYPE } from '../../../../constants/businessTarget';

/**
 * 建團隊收入模擬器 — the numbers behind IncomeSimulator, kept free of React so
 * they can be tested on their own. Two audiences:
 *
 * - `new`: someone not yet in the industry. Personal business only (first-year
 *   plus renewal commission) against staying in their current job.
 * - `team`: an adviser already in the industry who has not started recruiting.
 *   Their own income plus overriding from a team that grows by a net number of
 *   members a year (optionally a simple second generation), against their own
 *   income alone.
 *
 * Illustration only — every rate is an editable assumption, not a company's
 * actual scheme.
 */

export const SIM_YEARS = 5;
/** Renewal commission is counted for policy years 2 to 5. */
export const RENEWAL_YEARS = 4;
/** MDRT commission requirement — the figure the app's own MDRT calculator uses. */
export const MDRT_COMMISSION: number = TARGET_TYPE.MDRTC;

export type NewcomerInputs = {
  /** Cases closed a month. */
  cases: number;
  /** Average premium per case, per month (HK$). */
  monthlyPremium: number;
  /** Current monthly salary, for comparison (HK$). */
  salary: number;
  /** First-year commission rate, % of annual premium. */
  fycRate: number;
  /** Renewal commission rate, % of annual premium a year. */
  renewalRate: number;
  /** Business growth a year, %. */
  growth: number;
  /** Current job's yearly raise, %. */
  raise: number;
};

export type TeamInputs = {
  /** The adviser's own monthly income today (HK$). */
  ownMonthly: number;
  /** Net new members a year (after anyone who leaves). */
  addPerYear: number;
  /** A member's average monthly income (HK$). */
  memberMonthly: number;
  /** Direct overriding, % of members' income. */
  directRate: number;
  /** Count a simple second generation. */
  secondGeneration: boolean;
  /** Second-generation overriding, %. */
  secondRate: number;
  /** Second generation: new people each member brings a year, from their second year. */
  secondPerMember: number;
  /** The adviser's own income growth a year, %. */
  growth: number;
};

export const NEWCOMER_DEFAULTS: NewcomerInputs = {
  cases: 4,
  monthlyPremium: 2500,
  salary: 20000,
  fycRate: 35,
  renewalRate: 5,
  growth: 15,
  raise: 3
};

export const TEAM_DEFAULTS: TeamInputs = {
  ownMonthly: 40000,
  addPerYear: 4,
  memberMonthly: 25000,
  directRate: 15,
  secondGeneration: true,
  secondRate: 5,
  secondPerMember: 1,
  growth: 5
};

export type NewcomerYear = { firstYear: number; renewal: number; total: number; salary: number };
export type TeamYear = {
  own: number;
  direct: number;
  second: number;
  total: number;
  directMembers: number;
  secondMembers: number;
};

const pct = (value: number) => (Number.isFinite(value) ? value / 100 : 0);
const num = (value: number) => (Number.isFinite(value) && value > 0 ? value : 0);

export const simulateNewcomer = (x: NewcomerInputs): NewcomerYear[] => {
  const annualPremium: number[] = [];
  const years: NewcomerYear[] = [];
  for (let y = 0; y < SIM_YEARS; y += 1) {
    annualPremium[y] = num(x.cases) * num(x.monthlyPremium) * 12 * 12 * Math.pow(1 + pct(x.growth), y);
    let renewal = 0;
    for (let k = 0; k < y; k += 1) {
      const policyAge = y - k; // 1 = the policy's second year
      if (policyAge <= RENEWAL_YEARS) renewal += annualPremium[k] * pct(x.renewalRate);
    }
    const firstYear = annualPremium[y] * pct(x.fycRate);
    years.push({
      firstYear,
      renewal,
      total: firstYear + renewal,
      salary: num(x.salary) * 12 * Math.pow(1 + pct(x.raise), y)
    });
  }
  return years;
};

export const simulateTeam = (x: TeamInputs): TeamYear[] => {
  const memberYear = num(x.memberMonthly) * 12;
  const add = num(x.addPerYear);
  const years: TeamYear[] = [];
  for (let y = 0; y < SIM_YEARS; y += 1) {
    const directMembers = add * (y + 1);
    const directEarn = directMembers * memberYear;
    let secondMembers = 0;
    let secondEarn = 0;
    if (x.secondGeneration) {
      for (let s = 1; s <= y; s += 1) {
        const joined = add * s * num(x.secondPerMember); // members already there a full year bring recruits
        secondMembers += joined;
        secondEarn += joined * memberYear;
      }
    }
    const own = num(x.ownMonthly) * 12 * Math.pow(1 + pct(x.growth), y);
    const direct = directEarn * pct(x.directRate);
    const second = secondEarn * pct(x.secondRate);
    years.push({ own, direct, second, total: own + direct + second, directMembers, secondMembers });
  }
  return years;
};

export const formatMoney = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`;

/**
 * A large amount in short form. Chinese: 萬 with one decimal below 100 萬, whole above.
 * Otherwise $K, or $M from a million up (the unit 萬 means nothing to an English reader).
 */
export const formatWan = (value: number, chinese = true) => {
  if (chinese) {
    const wan = value / 10000;
    const rounded = Math.abs(wan) >= 100 ? Math.round(wan) : Math.round(wan * 10) / 10;
    return `${rounded.toLocaleString('en-US')} 萬`;
  }
  if (Math.abs(value) >= 1000000) return `$${Math.round(value / 100000) / 10}M`;
  return value === 0 ? '$0' : `$${Math.round(value / 1000).toLocaleString('en-US')}K`;
};

export const formatPeople = (value: number) => (Math.round(value * 10) / 10).toLocaleString('en-US');

/** A round axis maximum at or above `value`. */
export const niceMax = (value: number) => {
  if (value <= 0) return 10000;
  const power = Math.pow(10, Math.floor(Math.log10(value)));
  const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find(s => s * power >= value) || 10;
  return step * power;
};

/** 「+$27,000」 and 「+6%」 for a difference against a reference. */
export const differenceOf = (value: number, reference: number) => {
  const diff = value - reference;
  const sign = diff >= 0 ? '+' : '−';
  const percent = reference > 0 ? Math.round((Math.abs(diff) / reference) * 100) : 0;
  return { amount: `${sign}${formatMoney(Math.abs(diff))}`, percent: `${sign}${percent}%` };
};
